/**
 * REDEX Attention Engine - Frontend Service
 * 
 * API client for the compounding demand capture system.
 */

import api from './api';

// ==========================================
// TYPES
// ==========================================

export interface Industry {
  id: string;
  name: string;
  slug: string;
  description?: string;
  complianceRules: Record<string, any>;
  languageConstraints: Record<string, any>;
  disclaimers: string[];
  isRegulated: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface OntologyItem {
  id: string;
  industryId: string;
  type: 'primitive' | 'scenario' | 'modifier' | 'context';
  name: string;
  slug: string;
  description?: string;
  parentId?: string;
  hierarchyLevel: number;
  attributes: Record<string, any>;
  combinations: string[];
  weight: number;
  isActive: boolean;
  children?: OntologyItem[];
}

export interface Page {
  id: string;
  industryId: string;
  templateId?: string;
  slug: string;
  url: string;
  title: string;
  metaDescription: string;
  content: string;
  ontologyPath: string[];
  intentVector: Record<string, any>;
  score: number;
  impressions: number;
  conversions: number;
  scoreTrend: number;
  contentHash: string;
  status: 'draft' | 'published' | 'paused' | 'archived';
  publishedAt?: string;
  lastScoredAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Event {
  id: string;
  pageId: string;
  sessionId: string;
  eventType: string;
  eventData: Record<string, any>;
  weight: number;
  createdAt: string;
}

export interface Conversion {
  id: string;
  pageId: string;
  eventId?: string;
  sessionId: string;
  conversionType: string;
  value: number;
  metadata: Record<string, any>;
  createdAt: string;
}

export interface Allocation {
  id: string;
  industryId: string;
  pageId?: string;
  resourceType: string;
  amount: number;
  kellyFraction: number;
  winProbability: number;
  expectedOdds: number;
  confidence: number;
  periodStart: string;
  periodEnd: string;
  actualReturn?: number;
  status: 'pending' | 'active' | 'completed';
}

export interface Competitor {
  id: string;
  industryId: string;
  domain: string;
  name?: string;
  estimatedTraffic?: number;
  domainAuthority?: number;
  overlappingKeywords: number;
  threatLevel: 'low' | 'medium' | 'high';
  lastAnalyzed?: string;
}

export interface Template {
  id: string;
  industryId: string;
  name: string;
  slug: string;
  structure: Record<string, any>;
  variables: string[];
  isDefault: boolean;
}

export interface DashboardData {
  totalPages: number;
  totalImpressions: number;
  totalConversions: number;
  attentionCagr: number;
  conversionDensity: number;
  industriesSummary: IndustrySummary[];
}

export interface IndustrySummary {
  id: string;
  name: string;
  activePages: number;
  impressions: number;
  conversions: number;
  avgScore: number;
  trend: 'rising' | 'stable' | 'falling';
}

export interface PageMatrix {
  pages: PageMatrixItem[];
  totalPages: number;
}

export interface PageMatrixItem {
  id: string;
  slug: string;
  url: string;
  title: string;
  score: number;
  impressions: number;
  conversions: number;
  trend: number;
  status: string;
  ontologyPath: string[];
}

export interface CoverageGap {
  primitive: string;
  context: string;
  modifier?: string;
  estimatedVolume?: number;
  competition?: string;
}

export interface CoverageAnalysis {
  missingTypes: string[];
  lowCoverageTypes: Array<{ type: string; count: number }>;
  orphanedItems: string[];
  suggestions: string[];
}

export interface WinningPattern {
  pattern: string[];
  avgScore: number;
  totalConversions: number;
  pageCount: number;
}

// ==========================================
// INDUSTRIES
// ==========================================

export const getIndustries = async (activeOnly = true): Promise<Industry[]> => {
  const { data } = await api.get('/attention-engine/industries', { params: { activeOnly } });
  return data.data;
};

export const getIndustry = async (id: string): Promise<Industry> => {
  const { data } = await api.get(`/attention-engine/industries/${id}`);
  return data.data;
};

export const createIndustry = async (industry: Partial<Industry>): Promise<Industry> => {
  const { data } = await api.post('/attention-engine/industries', industry);
  return data.data;
};

export const updateIndustry = async (id: string, updates: Partial<Industry>): Promise<Industry> => {
  const { data } = await api.put(`/attention-engine/industries/${id}`, updates);
  return data.data;
};

export const deleteIndustry = async (id: string): Promise<void> => {
  await api.delete(`/attention-engine/industries/${id}`);
};

// ==========================================
// ONTOLOGY
// ==========================================

export const getOntologyItems = async (industryId: string, type?: string): Promise<OntologyItem[]> => {
  const { data } = await api.get('/attention-engine/ontology', { params: { industryId, type } });
  return data.data;
};

export const getOntologyTree = async (industryId: string): Promise<OntologyItem[]> => {
  const { data } = await api.get('/attention-engine/ontology/tree', { params: { industryId } });
  return data.data;
};

export const createOntologyItem = async (item: Partial<OntologyItem>): Promise<OntologyItem> => {
  const { data } = await api.post('/attention-engine/ontology', item);
  return data.data;
};

export const updateOntologyItem = async (id: string, updates: Partial<OntologyItem>): Promise<OntologyItem> => {
  const { data } = await api.put(`/attention-engine/ontology/${id}`, updates);
  return data.data;
};

export const deleteOntologyItem = async (id: string): Promise<void> => {
  await api.delete(`/attention-engine/ontology/${id}`);
};

export const generateIntentCombinations = async (industryId: string): Promise<string[][]> => {
  const { data } = await api.post(`/attention-engine/ontology/combinations/${industryId}`);
  return data.data;
};

export const analyzeCoverageGaps = async (industryId: string): Promise<CoverageGap[]> => {
  const { data } = await api.get('/attention-engine/ontology/coverage', { params: { industryId } });
  const analysis = data.data as CoverageAnalysis;
  
  // Transform the analysis into CoverageGap array for UI
  const gaps: CoverageGap[] = [];
  
  // Add missing types as gaps
  if (analysis.missingTypes) {
    analysis.missingTypes.forEach(type => {
      gaps.push({
        primitive: type,
        context: 'Missing Type',
        modifier: undefined,
        estimatedVolume: undefined,
        competition: 'unknown',
      });
    });
  }
  
  // Add low coverage types as gaps
  if (analysis.lowCoverageTypes) {
    analysis.lowCoverageTypes.forEach(item => {
      gaps.push({
        primitive: item.type,
        context: `Low Coverage (${item.count} items)`,
        modifier: undefined,
        estimatedVolume: undefined,
        competition: 'low',
      });
    });
  }
  
  // Add suggestions as gaps
  if (analysis.suggestions) {
    analysis.suggestions.forEach(suggestion => {
      gaps.push({
        primitive: 'Suggestion',
        context: suggestion,
        modifier: undefined,
        estimatedVolume: undefined,
        competition: undefined,
      });
    });
  }
  
  return gaps;
};

// ==========================================
// PAGES
// ==========================================

export const getPages = async (industryId?: string, status?: string): Promise<Page[]> => {
  const { data } = await api.get('/attention-engine/pages', { params: { industryId, status } });
  return data.data;
};

export const getPage = async (id: string): Promise<Page> => {
  const { data } = await api.get(`/attention-engine/pages/${id}`);
  return data.data;
};

export const createPage = async (page: Partial<Page>): Promise<Page> => {
  const { data } = await api.post('/attention-engine/pages', page);
  return data.data;
};

export const updatePage = async (id: string, updates: Partial<Page>): Promise<Page> => {
  const { data } = await api.put(`/attention-engine/pages/${id}`, updates);
  return data.data;
};

export const deletePage = async (id: string): Promise<void> => {
  await api.delete(`/attention-engine/pages/${id}`);
};

export const publishPage = async (id: string): Promise<Page> => {
  const { data } = await api.post(`/attention-engine/pages/${id}/publish`);
  return data.data;
};

export const pausePage = async (id: string): Promise<Page> => {
  const { data } = await api.post(`/attention-engine/pages/${id}/pause`);
  return data.data;
};

export const getPageMatrix = async (industryId: string, options?: {
  sortBy?: string;
  sortOrder?: string;
  limit?: number;
  offset?: number;
}): Promise<PageMatrix> => {
  const { data } = await api.get(`/attention-engine/pages/matrix/${industryId}`, { params: options });
  
  // Backend returns array directly, transform to PageMatrix structure
  const pagesArray = data.data;
  if (Array.isArray(pagesArray)) {
    return {
      pages: pagesArray.map((p: any) => ({
        id: p.id || p.pageId,
        slug: p.slug || '',
        url: p.url || '',
        title: p.title || p.slug || 'Untitled',
        score: p.score || p.currentScore || 0,
        impressions: p.impressions || 0,
        conversions: p.conversions || 0,
        trend: p.trend === 'rising' ? 1 : p.trend === 'falling' ? -1 : 0,
        status: p.status || 'active',
        ontologyPath: p.ontologyPath || []
      })),
      totalPages: pagesArray.length
    };
  }
  
  // If already in correct format, return as-is
  return data.data;
};

// ==========================================
// EVENTS & CONVERSIONS
// ==========================================

export const recordEvent = async (pageId: string, sessionId: string, eventType: string, eventData?: Record<string, any>): Promise<Event> => {
  const { data } = await api.post('/attention-engine/events', { pageId, sessionId, eventType, eventData });
  return data.data;
};

export const recordConversion = async (pageId: string, sessionId: string, conversionType: string, value?: number, metadata?: Record<string, any>): Promise<Conversion> => {
  const { data } = await api.post('/attention-engine/conversions', { pageId, sessionId, conversionType, value, metadata });
  return data.data;
};

export const getEventWeights = async (industryId?: string): Promise<Record<string, Record<string, number>>> => {
  const { data } = await api.get('/attention-engine/events/weights', { params: { industryId } });
  return data.data;
};

export const updateEventWeight = async (category: string, eventType: string, weight: number, industryId?: string): Promise<void> => {
  await api.put('/attention-engine/events/weights', { category, eventType, weight, industryId });
};

// ==========================================
// SCORING
// ==========================================

export const calculatePageScore = async (pageId: string): Promise<{ score: number; trend: number }> => {
  const { data } = await api.post(`/attention-engine/scoring/page/${pageId}`);
  return data.data;
};

export const runScoringCycle = async (industryId?: string): Promise<{ pagesScored: number; avgScore: number }> => {
  const { data } = await api.post('/attention-engine/scoring/cycle', { industryId: industryId || undefined });
  return data.data || { pagesScored: 0, avgScore: 0 };
};

// Backend response structure for winning patterns
interface WinningPatternsResponse {
  topScenarios: Array<{ scenario: string; avgScore: number; count: number }>;
  topContexts: Array<{ context: string; avgScore: number; count: number }>;
  optimalOntologyCombinations: string[][];
}

export const getWinningPatterns = async (industryId: string, limit?: number): Promise<WinningPattern[]> => {
  const { data } = await api.get(`/attention-engine/scoring/patterns/${industryId}`, { params: { limit } });
  const response = data.data as WinningPatternsResponse;
  
  // Transform backend response to WinningPattern[] format
  const patterns: WinningPattern[] = [];
  
  // Add scenario-based patterns
  if (response.topScenarios && Array.isArray(response.topScenarios)) {
    response.topScenarios.forEach(s => {
      patterns.push({
        pattern: ['Scenario', s.scenario],
        avgScore: s.avgScore,
        totalConversions: 0, // Not available from this endpoint
        pageCount: s.count
      });
    });
  }
  
  // Add context-based patterns  
  if (response.topContexts && Array.isArray(response.topContexts)) {
    response.topContexts.forEach(c => {
      patterns.push({
        pattern: ['Context', c.context],
        avgScore: c.avgScore,
        totalConversions: 0,
        pageCount: c.count
      });
    });
  }
  
  // Add ontology combination patterns
  if (response.optimalOntologyCombinations && Array.isArray(response.optimalOntologyCombinations)) {
    response.optimalOntologyCombinations.slice(0, 5).forEach(combo => {
      patterns.push({
        pattern: ['Ontology Combo', ...combo.slice(0, 3)],
        avgScore: 0, // Score not available for combos
        totalConversions: 0,
        pageCount: 1
      });
    });
  }
  
  // Sort by avgScore descending and return
  return patterns.sort((a, b) => b.avgScore - a.avgScore);
};

export const getPromotionCandidates = async (industryId: string, limit?: number): Promise<Page[]> => {
  const { data } = await api.get(`/attention-engine/scoring/promotions/${industryId}`, { params: { limit } });
  return data.data;
};

// ==========================================
// ALLOCATION (KELLY)
// ==========================================

export const getAllocations = async (industryId?: string, status?: string): Promise<Allocation[]> => {
  const { data } = await api.get('/attention-engine/allocations', { params: { industryId, status } });
  return data.data;
};

export const runAllocationCycle = async (industryId: string, budget: number, resourceType?: string): Promise<Allocation[]> => {
  const { data } = await api.post('/attention-engine/allocations/cycle', { industryId, budget, resourceType });
  return data.data;
};

export const recordAllocationReturn = async (allocationId: string, actualReturn: number): Promise<Allocation> => {
  const { data } = await api.post(`/attention-engine/allocations/${allocationId}/return`, { actualReturn });
  return data.data;
};

export const getPortfolioMetrics = async (industryId: string): Promise<{
  expectedReturn: number;
  riskLevel: number;
  diversificationScore: number;
  kellyUtilization: number;
}> => {
  const { data } = await api.get(`/attention-engine/allocations/portfolio/${industryId}`);
  return data.data;
};

// ==========================================
// COMPLIANCE
// ==========================================

export const checkCompliance = async (industryId: string, content: string): Promise<{
  isCompliant: boolean;
  issues: Array<{ type: string; message: string; severity: string }>;
}> => {
  const { data } = await api.post('/attention-engine/compliance/check', { industryId, content });
  return data.data;
};

export const autoFixContent = async (industryId: string, content: string): Promise<{
  fixedContent: string;
  changesApplied: string[];
}> => {
  const { data } = await api.post('/attention-engine/compliance/autofix', { industryId, content });
  return data.data;
};

export const getCompliancePresets = async (): Promise<string[]> => {
  const { data } = await api.get('/attention-engine/compliance/presets');
  return data.data;
};

export const applyCompliancePreset = async (industryId: string, presetName: string): Promise<Industry> => {
  const { data } = await api.post('/attention-engine/compliance/apply-preset', { industryId, presetName });
  return data.data;
};

// ==========================================
// CONTENT GENERATION
// ==========================================

export const generateFromOntology = async (industryId: string, ontologySlugs: string[], templateId?: string): Promise<Page> => {
  const { data } = await api.post('/attention-engine/content/generate', { industryId, ontologySlugs, templateId });
  return data.data;
};

export const batchGenerate = async (industryId: string, combinations: string[][], templateId?: string): Promise<Page[]> => {
  const { data } = await api.post('/attention-engine/content/batch', { industryId, combinations, templateId });
  return data.data;
};

export const generateLocationVariations = async (pageId: string, locations: string[]): Promise<Page[]> => {
  const { data } = await api.post('/attention-engine/content/location-variations', { pageId, locations });
  return data.data;
};

export const clonePage = async (pageId: string, newSlug: string, modifications?: Partial<Page>): Promise<Page> => {
  const { data } = await api.post(`/attention-engine/content/clone/${pageId}`, { newSlug, modifications });
  return data.data;
};

// ==========================================
// TEMPLATES
// ==========================================

export const getTemplates = async (industryId?: string): Promise<Template[]> => {
  const { data } = await api.get('/attention-engine/templates', { params: { industryId } });
  return data.data;
};

export const getTemplate = async (id: string): Promise<Template> => {
  const { data } = await api.get(`/attention-engine/templates/${id}`);
  return data.data;
};

export const createTemplate = async (template: Partial<Template>): Promise<Template> => {
  const { data } = await api.post('/attention-engine/templates', template);
  return data.data;
};

export const updateTemplate = async (id: string, updates: Partial<Template>): Promise<Template> => {
  const { data } = await api.put(`/attention-engine/templates/${id}`, updates);
  return data.data;
};

export const deleteTemplate = async (id: string): Promise<void> => {
  await api.delete(`/attention-engine/templates/${id}`);
};

// ==========================================
// COMPETITORS
// ==========================================

export const getCompetitors = async (industryId: string): Promise<Competitor[]> => {
  const { data } = await api.get('/attention-engine/competitors', { params: { industryId } });
  return data.data;
};

export const addCompetitor = async (competitor: Partial<Competitor>): Promise<Competitor> => {
  const { data } = await api.post('/attention-engine/competitors', competitor);
  return data.data;
};

export const analyzeCompetitor = async (id: string): Promise<Competitor> => {
  const { data } = await api.post(`/attention-engine/competitors/${id}/analyze`);
  return data.data;
};

export const deleteCompetitor = async (id: string): Promise<void> => {
  await api.delete(`/attention-engine/competitors/${id}`);
};

// ==========================================
// DASHBOARD
// ==========================================

export const getDashboardData = async (industryId?: string): Promise<DashboardData> => {
  const { data } = await api.get('/attention-engine/dashboard/global', { params: { industryId } });
  return data.data;
};

export const getIndustryDashboard = async (industryId: string): Promise<{
  industry: Industry;
  pages: PageMatrixItem[];
  ontologyStats: { primitives: number; scenarios: number; modifiers: number; contexts: number };
  recentEvents: number;
  recentConversions: number;
  portfolioMetrics: any;
}> => {
  const { data } = await api.get(`/attention-engine/dashboard/industry/${industryId}`);
  return data.data;
};

// ==========================================
// EXPORT
// ==========================================

export const attentionEngineService = {
  // Industries
  getIndustries,
  getIndustry,
  createIndustry,
  updateIndustry,
  deleteIndustry,
  
  // Ontology
  getOntologyItems,
  getOntologyTree,
  createOntologyItem,
  updateOntologyItem,
  deleteOntologyItem,
  generateIntentCombinations,
  analyzeCoverageGaps,
  
  // Pages
  getPages,
  getPage,
  createPage,
  updatePage,
  deletePage,
  publishPage,
  pausePage,
  getPageMatrix,
  
  // Events & Conversions
  recordEvent,
  recordConversion,
  getEventWeights,
  updateEventWeight,
  
  // Scoring
  calculatePageScore,
  runScoringCycle,
  getWinningPatterns,
  getPromotionCandidates,
  
  // Allocation
  getAllocations,
  runAllocationCycle,
  recordAllocationReturn,
  getPortfolioMetrics,
  
  // Compliance
  checkCompliance,
  autoFixContent,
  getCompliancePresets,
  applyCompliancePreset,
  
  // Content
  generateFromOntology,
  batchGenerate,
  generateLocationVariations,
  clonePage,
  
  // Templates
  getTemplates,
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  
  // Competitors
  getCompetitors,
  addCompetitor,
  analyzeCompetitor,
  deleteCompetitor,
  
  // Dashboard
  getDashboardData,
  getIndustryDashboard,
};

export default attentionEngineService;
